import React, { useCallback, useEffect, useState } from 'react';
import { apiBase, authHeaders, resolveOrgId } from './utils/authHeaders';
import PipelineBulkUpload from './components/PipelineBulkUpload';

const API_URL = apiBase();

const GL_DEFAULTS = [
  'fruit_veg', 'dairy', 'meat_poultry', 'fish', 'dry_goods_grains',
  'condiments_spices', 'packaging', 'cleaning', 'other',
];

/** Pure helpers — tested without mounting the full page. */
export function groupTasksByType(tasks) {
  const groups = { extraction: [], sku_match: [], gl_category: [], unit: [], other: [] };
  for (const t of [...(tasks || [])].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''))) {
    const key = groups[t.type] ? t.type : 'other';
    groups[key].push(t);
  }
  return groups;
}

export async function resolveTaskOptimistic(apiPost, taskId, payload, onRemove) {
  onRemove(taskId);
  try {
    await apiPost(taskId, payload);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err };
  }
}

export default function ReviewQueue() {
  const [orgId] = useState(() => resolveOrgId());
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [extractedEdit, setExtractedEdit] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/review?status=open`, { headers: authHeaders({ orgId, json: true }) });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks || []);
      }
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!active || active.type !== 'sku_match') return;
    const q = search || active.payload?.line?.raw_desc || '';
    if (!q) return;
    let cancelled = false;
    (async () => {
      const res = await fetch(
        `${API_URL}/api/review/candidates?q=${encodeURIComponent(q)}`,
        { headers: authHeaders({ orgId, json: true }) }
      );
      if (res.ok && !cancelled) {
        const data = await res.json();
        setCandidates(data.candidates || []);
      }
    })();
    return () => { cancelled = true; };
  }, [active, search, orgId]);

  const removeTask = (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    setActive((a) => (a && a.id === id ? null : a));
  };

  const postResolve = async (taskId, payload) => {
    const res = await fetch(`${API_URL}/api/review/${taskId}/resolve`, {
      method: 'POST',
      headers: authHeaders({ orgId, json: true }),
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const resolve = async (payload) => {
    if (!active) return;
    const taskId = active.id;
    const result = await resolveTaskOptimistic(postResolve, taskId, payload, removeTask);
    if (!result.ok) {
      setToast('Resolve failed — reloading queue');
      await load();
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!active) return;
      if (active.type === 'sku_match') {
        if (e.key >= '1' && e.key <= '5') {
          const idx = Number(e.key) - 1;
          const c = candidates[idx];
          if (c) {
            e.preventDefault();
            resolve({
              item_id: c.item_id,
              raw_desc: active.payload?.line?.raw_desc,
            });
          }
        }
        if (e.key === 'Enter' && candidates[0]) {
          e.preventDefault();
          resolve({
            item_id: candidates[0].item_id,
            raw_desc: active.payload?.line?.raw_desc,
          });
        }
        if (e.key === 'n' || e.key === 'N') {
          e.preventDefault();
          const name = active.payload?.line?.raw_desc || search || 'New item';
          fetch(`${API_URL}/api/review/items`, {
            method: 'POST',
            headers: authHeaders({ orgId, json: true }),
            body: JSON.stringify({ name, unit: active.payload?.line?.unit || 'ea' }),
          })
            .then((r) => r.json())
            .then((item) => resolve({ item_id: item.id, raw_desc: name }));
        }
        if (e.key === 's' || e.key === 'S') {
          e.preventDefault();
          removeTask(active.id);
          setActive(null);
        }
      }
      if (active.type === 'gl_category' && e.key >= '1' && e.key <= '9') {
        const cats = active.payload?.categories || GL_DEFAULTS;
        const cat = cats[Number(e.key) - 1];
        if (cat) {
          e.preventDefault();
          resolve({ gl_category: cat });
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const groups = groupTasksByType(tasks);

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="review-queue">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Review queue</h1>
        <p className="text-slate-600 mb-4 text-sm">Keyboard: 1–5 select · Enter confirm · N new item · S skip</p>
        <div className="mb-6">
          <PipelineBulkUpload compact onUploaded={() => load()} />
        </div>
        {toast && (
          <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded text-sm">{toast}</div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-4">
            {Object.entries(groups).map(([type, list]) => (
              list.length === 0 ? null : (
                <div key={type} className="bg-white border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="font-semibold text-sm uppercase tracking-wide text-slate-700">{type}</h2>
                    <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">{list.length}</span>
                  </div>
                  <ul className="space-y-1">
                    {list.map((t) => (
                      <li key={t.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setActive(t);
                            setSearch('');
                            setExtractedEdit(t.payload?.extracted || null);
                          }}
                          className={`w-full text-left text-sm px-2 py-1.5 rounded ${active?.id === t.id ? 'bg-emerald-50 text-emerald-900' : 'hover:bg-slate-50'}`}
                        >
                          {(t.payload?.line?.raw_desc || t.document_id || t.id).slice(0, 48)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            ))}
            {!loading && tasks.length === 0 && (
              <p className="text-slate-500 text-sm">Queue clear — nice work.</p>
            )}
          </div>

          <div className="lg:col-span-2 bg-white border rounded-lg p-5 min-h-[320px]">
            {!active && <p className="text-slate-400">Select a task</p>}
            {active?.type === 'sku_match' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Invoice line</h3>
                  <p className="text-lg">{active.payload?.line?.raw_desc}</p>
                  <p className="text-sm text-slate-600 mt-1">
                    qty {active.payload?.line?.qty} {active.payload?.line?.unit} · £{active.payload?.line?.unit_price}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Candidates</h3>
                  <input
                    className="w-full border rounded px-2 py-1.5 mb-2 text-sm"
                    placeholder="Search items"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <ol className="space-y-1">
                    {candidates.slice(0, 5).map((c, i) => (
                      <li key={c.item_id}>
                        <button
                          type="button"
                          className="w-full text-left border rounded px-2 py-1.5 text-sm hover:bg-emerald-50"
                          onClick={() => resolve({ item_id: c.item_id, raw_desc: active.payload?.line?.raw_desc })}
                        >
                          <span className="font-mono text-xs text-slate-400 mr-2">{i + 1}</span>
                          {c.name} <span className="text-slate-400">({Math.round(c.score * 100)}%)</span>
                        </button>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
            {active?.type === 'gl_category' && (
              <div>
                <h3 className="font-semibold mb-2">Assign GL category</h3>
                <p className="mb-3 text-sm">{active.payload?.line?.raw_desc}</p>
                <div className="flex flex-wrap gap-2">
                  {(active.payload?.categories || GL_DEFAULTS).map((cat, i) => (
                    <button
                      key={cat}
                      type="button"
                      className="border rounded px-3 py-1.5 text-sm hover:bg-emerald-50"
                      onClick={() => resolve({ gl_category: cat })}
                    >
                      <span className="font-mono text-xs mr-1">{i + 1}</span>{cat}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {active?.type === 'extraction' && (
              <div>
                <h3 className="font-semibold mb-2">Fix extraction</h3>
                <textarea
                  className="w-full border rounded p-2 font-mono text-xs h-48"
                  value={JSON.stringify(extractedEdit || active.payload?.extracted || {}, null, 2)}
                  onChange={(e) => {
                    try { setExtractedEdit(JSON.parse(e.target.value)); } catch { /* keep typing */ }
                  }}
                />
                <button
                  type="button"
                  className="mt-3 bg-emerald-700 text-white px-4 py-2 rounded text-sm"
                  onClick={() => resolve({ extracted: extractedEdit || active.payload?.extracted })}
                >
                  Save &amp; re-validate
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
